import {gql} from '@apollo/client';
import {Tooltip} from '@mantine/core';
import {css} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {styles as fieldActionButtonStyles} from 'metadata/FieldActionButtons';
import SettingsIconDropdown from 'nav/sidepanel/SettingsIconDropdown';
import {
  getFieldOptions,
  getInterfaceFieldOptions,
  LOADING_FIELD_OPTION,
} from 'registry/field/FieldTableOptionGetters';
import FieldTableRow, {DisablableControl} from 'registry/field/FieldTableRow';
import {getSnapshotFieldDisabledMessage} from 'registry/field/FieldUtil';
import {AssaySchemaFieldTableRowQuery} from 'results/assay-schemas/__generated/AssaySchemaFieldTableRowQuery';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {AdvancedSettingsDropdown} from 'schemas/fields/AdvancedSettingsDropdown';
import {AdvancedSettingsIconProps, getUnitDropdownOption} from 'schemas/fields/AdvancedSettingsIconUtils';
import {
  FieldOption,
  FieldType,
  isRequiredLinkFolderItemType,
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {getSnapshotFieldLookupConfigModalProps} from 'schemas/SnapshotFieldLookupConfigUtil';
import legacyRunModal from 'ui-library/legacyRunModal';
import UnitSelectionModal from 'units/UnitSelectionModal';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

export interface AssaySchemaFieldTableRowProps {
  tagSchemas: Array<TagSchemaSummaryGraphQL>;
  storableSchemas: Array<StorableSchema>;
  assaySchemas: Array<AssaySchema>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  schemaInterfaces: Array<SchemaInterface>;
  id: string;
  initialField: SchemaField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  ownerUrlId: string;
  registryId?: string | null;
  schema: AssaySchema | null;
  includeSnapshotField: boolean;
  showUnitConfigOption?: boolean;
  schemaHasData?: boolean;
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummaries?: Array<string> | null;
  shouldShowTooltipText?: boolean;
}

function fieldCanBeRelaxed(props: AssaySchemaFieldTableRowProps): boolean {
  return (
    isRequiredLinkTagSchema(props.initialField.requiredLink) ||
    isRequiredLinkSchemaInterface(props.initialField.requiredLink) ||
    isRequiredLinkFolderItemType(props.initialField.requiredLink)
  );
}

function getDisableFieldDefinitionDropdownTooltip(props: AssaySchemaFieldTableRowProps): string | null {
  if (props.schemaHasData && props.initialField.dbId != null && !fieldCanBeRelaxed(props)) {
    return "You can't change the field definition once data has been created by users. You may want to archive the field and create a new one instead.";
  } else if (
    props.schemaFieldDependentsSummaries != null &&
    props.schemaFieldDependentsSummaries.length > 0
  ) {
    return `You can't change the field definition because other fields depend on this one: ${props.schemaFieldDependentsSummaries.join(
      ', '
    )}`;
  }
  return null;
}

function getDisableIsRequiredCheckboxTooltip(props: AssaySchemaFieldTableRowProps): string | null {
  if (props.schemaHasData) {
    if (props.initialField.dbId == null) {
      return "You can't add a new required field once data has been created by users.";
    } else if (!props.initialField.isRequired) {
      return "You can't make a field required once data has been created by users.";
    }
  }
  return null;
}

function getDisableArchiveButtonTooltip(props: AssaySchemaFieldTableRowProps): string | null {
  if (props.initialField.isRequired) {
    return 'Required fields cannot be archived. Please mark the field as not required before archiving.';
  }
  return null;
}

function getDisableDeleteButtonTooltip(props: AssaySchemaFieldTableRowProps): string | null {
  if (props.schemaFieldDependentsSummaries != null && props.schemaFieldDependentsSummaries.length > 0) {
    return `You can't delete this field because other fields depend on it: ${props.schemaFieldDependentsSummaries.join(
      ', '
    )}`;
  }
  return null;
}

function getDisableIsMultiCheckboxTooltip(props: AssaySchemaFieldTableRowProps): string | null {
  if (props.schemaHasData && props.initialField.isMulti) {
    return "You can't change a field from multi-select to single-select once data has been created by users.";
  }
  if (props.schemaFieldDependentsSummaries != null && props.schemaFieldDependentsSummaries.length > 0) {
    return `You can't change whether the field is single-select or multi-select because other fields depend on this field: ${props.schemaFieldDependentsSummaries.join(
      ', '
    )}`;
  }
  return null;
}

export const ASSAY_SCHEMA_FIELD_TABLE_ROW_QUERY = gql`
  query AssaySchemaFieldTableRowQuery($schemaId: StrictString!) @skipAudit @engComponent(name: RESULTS) {
    tagSchema(id: $schemaId) {
      ... on TagSchemaNode {
        id
        interfaces {
          id
        }
      }
    }
  }
`;

function AssaySchemaFieldAdvancedSettingsIcon(props: {
  field: SchemaField;
  fieldId: string;
  schema: AssaySchema;
  schemaHasData: boolean;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
}): JSX.Element {
  const options = [
    {
      name: 'Set unit...',
      disabled: ![FieldType.FLOAT, FieldType.INTEGER].includes(props.field.fieldType),
      onSelect: () => {
        legacyRunModal(UnitSelectionModal, {
          existingUnitId: props.field.unitApiIdentifier,
          readOnly: Boolean(props.field.unitApiIdentifier && props.schemaHasData),
          readOnlyReason:
            "You can't change the unit once data has been created by users. You may want to archive the field and create a new one instead.",
          selectionWarningMessage:
            'You will not be able to change the unit once data has been created by users.',
          onRequestSubmit: ({unitApiIdentifier}) => {
            props.requestChange(props.fieldId, {...props.field, unitApiIdentifier});
          },
        });
      },
    },
  ];
  const dropdownDisabled = options.every((option) => option.disabled);
  return (
    <Tooltip label={dropdownDisabled ? 'Advanced settings do not apply to this field' : ''}>
      <span>
        <SettingsIconDropdown
          options={options.filter((option) => !option.disabled)}
          iconClassName={css(fieldActionButtonStyles.singleButton)}
          dropLeft={true}
          disabled={dropdownDisabled}
        />
      </span>
    </Tooltip>
  );
}

function AssaySchemaFieldAdvancedSettingsIconForFieldCreation(
  props: AdvancedSettingsIconProps
): JSX.Element | null {
  const options = [getUnitDropdownOption(props)];
  return <AdvancedSettingsDropdown options={options} />;
}

export function AssaySchemaFieldTableRow(props: AssaySchemaFieldTableRowProps): JSX.Element {
  const relaxationOnly = fieldCanBeRelaxed(props) && props.schemaHasData && props.initialField.dbId != null;
  const schemaId = isRequiredLinkTagSchema(props.initialField.requiredLink)
    ? props.initialField.requiredLink.tagSchema.id
    : '';
  const request = useSkippableBenchlingQuery<AssaySchemaFieldTableRowQuery>(
    ASSAY_SCHEMA_FIELD_TABLE_ROW_QUERY,
    {
      variables: {schemaId},
      skip: !relaxationOnly || schemaId === '',
    }
  );
  let availableFieldOptions: Array<FieldOption> = [];
  if (request.type === QueryResultType.SKIPPED && !relaxationOnly) {
    // Don't relax fields, include all options
    availableFieldOptions = getFieldOptions({
      initialField: props.initialField,
      schemaFieldSelectors: props.schemaFieldSelectors,
      storableSchemas: props.storableSchemas,
      assaySchemas: props.assaySchemas,
      tagSchemas: props.tagSchemas,
      schemaInterfaces: props.schemaInterfaces,
      includeJSONField: true,
      includeAssayLinks: true,
      includeBatchLinks: true,
      includeBooleanField: true,
      includeArchivedSchemas: false,
      includeSnapshotField: props.includeSnapshotField,
      snapshotFieldDisabledMessage: getSnapshotFieldDisabledMessage(props.schema, props.initialField),
    });
  } else if (request.type === QueryResultType.SKIPPED && relaxationOnly) {
    // Relax fields, include only the generic options
    availableFieldOptions = getInterfaceFieldOptions({
      initialField: props.initialField,
      schemaInterfaces: [],
      includeArchivedSchemaInterfaces: false,
    });
  } else if (request.type === QueryResultType.PENDING) {
    availableFieldOptions = [LOADING_FIELD_OPTION];
  } else if (request.type === QueryResultType.SUCCESS) {
    // Relax fields but include interfaces that are implemented by the currently linked schema.
    const implementedInterfacesIds =
      'interfaces' in request.data.tagSchema
        ? request.data.tagSchema.interfaces.map((schemaInterface) => schemaInterface.id)
        : [];
    availableFieldOptions = getInterfaceFieldOptions({
      initialField: props.initialField,
      schemaInterfaces: props.schemaInterfaces.filter((schemaInterface) =>
        implementedInterfacesIds.includes(schemaInterface.id)
      ),
      includeArchivedSchemaInterfaces: false,
    });
  }

  // TODO BNCH-87604: Change to true when we support other options
  const advancedSettingsComponentForFieldCreation = props.showUnitConfigOption
    ? AssaySchemaFieldAdvancedSettingsIconForFieldCreation
    : undefined;

  return (
    <FieldTableRow
      {...props}
      availableFieldOptions={availableFieldOptions}
      lookupConfigModalProps={getSnapshotFieldLookupConfigModalProps(props.schema)}
      disableControlsWithTooltips={{
        [DisablableControl.ARCHIVE_BUTTON]: getDisableArchiveButtonTooltip(props),
        [DisablableControl.DELETE_BUTTON]: getDisableDeleteButtonTooltip(props),
        [DisablableControl.FIELD_DEFINITION_DROPDOWN]: getDisableFieldDefinitionDropdownTooltip(props),
        [DisablableControl.IS_MULTI_CHECKBOX]: getDisableIsMultiCheckboxTooltip(props),
        [DisablableControl.IS_REQUIRED_CHECKBOX]: getDisableIsRequiredCheckboxTooltip(props),
      }}
      isUneditable={props.initialField.isUneditable}
      shouldShowRelaxationWarning={relaxationOnly}
      shouldShowTooltipText={props.shouldShowTooltipText}
      extraActionButtons={{
        viewMode:
          props.schema && props.showUnitConfigOption
            ? [
                <AssaySchemaFieldAdvancedSettingsIcon
                  field={props.initialField}
                  fieldId={props.id}
                  schema={props.schema}
                  requestChange={props.requestChange}
                  schemaHasData={props.schemaHasData ?? false}
                  key="advanced-settings"
                />,
              ]
            : [],
        editMode: [],
      }}
      advancedSettingsComponentForFieldCreation={advancedSettingsComponentForFieldCreation}
      schemaId={props.schema?.id ?? null}
    />
  );
}

export function ResultSchemaFieldTableRow(
  props: Omit<AssaySchemaFieldTableRowProps, 'includeSnapshotField'>
): JSX.Element {
  return (
    <AssaySchemaFieldTableRow
      {...props}
      includeSnapshotField={BenchlingConfig.RESULTS_ENABLE_RESULTS_AGAINST_CONTAINERS_FEATURES}
      showUnitConfigOption={true}
      shouldShowTooltipText={true}
    />
  );
}

export function RunSchemaFieldTableRow(
  props: Omit<AssaySchemaFieldTableRowProps, 'includeSnapshotField'>
): JSX.Element {
  return (
    <AssaySchemaFieldTableRow
      {...props}
      includeSnapshotField={false}
      showUnitConfigOption={false}
      shouldShowTooltipText={true}
    />
  );
}
